import {getRestRoute, getSetting, setSetting} from "@wc-stripe/utils";
import apiFetch from "@wordpress/api-fetch";
import actions from '@wc-stripe/actions';

class Cart {

    processing = false;

    constructor() {
        // Initialize shared actions instance
        this.actions = actions;
        this.data = getSetting('cart');
        this.page = getSetting('page');
        this.actions.addAction('cart_dom_updated', this.onCartUpdated.bind(this));
        this.actions.addAction('updated_checkout', this.onUpdatedCheckout.bind(this));
        this.actions.addAction('cart_fragments_changed', this.onCartFragmentsChanged.bind(this));
        this.actions.addAction('added_to_cart', this.onAddedToCart.bind(this));
        this.actions.addAction('removed_from_cart', this.onRemovedFromCart.bind(this));
        this.actions.addAction('product_updated', this.onProductUpdated.bind(this));
        this.actions.addAction('mini_cart_updated', this.onMiniCartUpdated.bind(this));
    }

    update(data = {}) {
        try {
            this._prevData = {...this.data};
            this.data = {...this.data, ...data};
            this.actions.doAction('cart_updated', this, data);
        } catch (e) {
            console.error(e);
        }
    }

    hasChanged(key) {
        return this._prevData?.[key] !== this.data[key];
    }

    async onCartUpdated(e, data = null) {
        if (data?.cart) {
            this.update(data.cart);
        } else {
            await this.refreshData();
        }
    }

    onMiniCartUpdated(cart) {
        if (cart.extensions?.wc_stripe?.cart) {
            this.update(cart.extensions.wc_stripe?.cart);
        }
    }

    applyFilters(event, ...args) {
        return this.actions.applyFilters(event, ...args);
    }

    async onUpdatedCheckout(e, response) {
        let data = {};
        if (response?.fragments?.wc_stripe_data) {
            data = response.fragments.wc_stripe_data;
            const {cart, sdkParams = null} = data;
            this.data = {...this.data, ...cart};
            if (sdkParams) {
                setSetting('sdkParams', queryParams);
            }
        }
    }

    onCartFragmentsChanged() {
        setTimeout(() => {
            if (window.wcStripeMiniCartData) {
                this.update(window.wcStripeMiniCartData);
                window.wcStripeMiniCartData = null;
            }
        }, 250);
    }

    onAddedToCart(e, data = {}) {
        if (data?.cart) {
            this.update(data.cart);
        }
    }

    onRemovedFromCart(e, data = null) {
        if (data?.cart) {
            this.update(data.cart);
        }
    }

    async onProductUpdated(product) {
        if (!product.needsCartCalculation()) {
            return;
        }
        if (!this.actions.applyFilters('cart_calculation_enabled', false, product, this)) {
            return;
        }
        await this.cartCalculation();
    }

    getCurrency() {
        return this.actions.applyFilters('cart_get_currency', this.data.currency, this);
    }

    getCountryCode() {
        return this.data.countryCode;
    }

    needsShipping() {
        return this.actions.applyFilters('cart_needs_shipping', this.data?.needsShipping, this);
    }

    isEmpty() {
        return this.data?.isEmpty;
    }

    isPaymentMethodAvailable(id) {
        return this.data.paymentMethods?.[id]?.available ?? false;
    }

    async refreshData(data = null) {
        if (!this.processing) {
            try {
                this.processing = true;
                const response = await apiFetch({
                    method: 'POST',
                    url: getRestRoute('cart/refresh'),
                    data: {page: this.page},
                });
                this.update(response.cart);
                return response;
            } catch (error) {
                console.log(error);
            } finally {
                this.processing = false;
            }
        }
    }

    async addToCart(data) {
        return apiFetch({
            method: 'POST',
            url: getRestRoute('cart/item'),
            data
        }).then(response => {
            if (response.cart) {
                this.update(response.cart);
            }
            return response;
        }).catch(error => {
            throw error;
        })
    }

    removeCartItem(key) {
        this.actions.doAction('before_remove_cart_item', this);
        return apiFetch({
            method: 'DELETE',
            url: getRestRoute('cart/item'),
            data: {
                key
            }
        }).then(response => {
            this.update(response.cart);
        }).catch(error => {
            console.log('Error removing item from cart. ', error);
        }).finally(() => {
            this.actions.doAction('after_remove_cart_item', this);
        });
    }

    /**
     * Update shipping method and/or address
     * @param {Object} data - Shipping update data
     * @param {Object} data.address - Shipping address (city, postcode, state, country)
     * @param {string} data.shipping_method - Selected shipping method ID
     * @returns {Promise<Object>} Updated cart data with totals and shipping options
     */
    async updateShipping(data) {
        return apiFetch({
            method: 'POST',
            url: getRestRoute('cart/shipping'),
            data
        }).then(response => {
            if (!response.code) {
                this.update(response);
            }
            return response;
        }).catch(error => {
            throw error;
        });
    }

    /**
     *
     * @param {Object} data
     * @returns {Promise<void>}
     */
    async updateBilling(data) {
        return apiFetch({
            method: 'POST',
            url: getRestRoute('cart/billing'),
            data
        }).then(response => {
            if (!response.code) {
                this.update(response.cart);
            }
            return response;
        }).catch(error => {
            throw error;
        });
    }

    /**
     * Calculate cart totals for a product without actually adding it to cart
     * Used on product pages to preview totals with the product
     * Debounced to prevent rapid successive calls
     *
     * @param {Object} data - Product data (product_id, variation_id, qty, variation, payment_method)
     */
    cartCalculation(data = {}) {
        // Clear any pending timeout from previous calls
        clearTimeout(this._calculationTimeout);

        // Emit action when calculation is about to start
        this.actions.doAction('before_cart_calculation', data, this);

        // Debounce the API call
        this._calculationTimeout = setTimeout(async () => {
            try {
                const response = await apiFetch({
                    method: 'POST',
                    url: getRestRoute('cart/calculation'),
                    data: this.actions.applyFilters('cart_calculation_data', data, this)
                });
                if (response.cart) {
                    this.update(response.cart);
                }
            } catch (error) {
                console.error('Cart calculation error:', error);
            } finally {
                this.actions.doAction('after_cart_calculation', this);
            }
        }, 250);
    }

    getTotal(type = 'string') {
        let total = this.data?.total || 0.00;
        return this.actions.applyFilters('cart_total', total, this);
    }

    getTotalCents() {
        return this.actions.applyFilters('cart_total_cents', parseInt(this.data?.totalCents || 0), this);
    }

    getSubtotalCents() {
        return this.actions.applyFilters('cart_subtotal_cents', parseInt(this.data?.subtotalCents || 0), this);
    }

    getLineItems() {
        return this.data.lineItems || [];
    }

    getShippingOptions() {
        return this.data.shippingOptions || [];
    }

    getSelectedShippingMethod() {
        return this.data.selectedShippingMethod || '';
    }
}

export default new Cart();